import type {ReactNode} from 'react';
import {createContext, useContext, useState} from 'react';

import ConfirmDialog from './ConfirmDialog';

export type ConfirmOptions = {
  title?: string;
  message?: string;
  isWarning?: boolean;
};

export type ConfirmContextType = {
  confirm: (options?: ConfirmOptions) => Promise<void>;
};

export type ConfirmProviderProps = {
  children?: ReactNode;
};

// eslint-disable-next-line react-refresh/only-export-components
export const ConfirmContext = createContext<ConfirmContextType>({
  confirm: () => Promise.reject(new Error('Context not initialized')),
});

export const ConfirmProvider = ({children}: ConfirmProviderProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [promiseInfo, setPromiseInfo] = useState<{
    resolve: (value: void | PromiseLike<void>) => void;
    reject: (reason?: unknown) => void;
  } | null>(null);

  const [confirmOptions, setConfirmOptions] = useState<
    ConfirmOptions | undefined
  >();

  const confirm = (options?: ConfirmOptions): Promise<void> => {
    return new Promise((resolve, reject) => {
      setPromiseInfo({
        resolve,
        reject,
      });
      setConfirmOptions(options);
      setIsOpen(true);
    });
  };

  const handleClose = () => {
    promiseInfo?.reject('Confirm dialog rejected');
    setIsOpen(false);
    setPromiseInfo(null);
  };

  const handleConfirm = () => {
    promiseInfo?.resolve();
    setIsOpen(false);
    setPromiseInfo(null);
  };

  return (
    <ConfirmContext.Provider value={{confirm}}>
      {children}
      <ConfirmDialog
        open={isOpen}
        onClose={handleClose}
        onConfirm={handleConfirm}
        title={confirmOptions?.title}
        message={confirmOptions?.message}
        isWarning={confirmOptions?.isWarning}
      />
    </ConfirmContext.Provider>
  );
};

// eslint-disable-next-line react-refresh/only-export-components
export const useConfirm = () => useContext(ConfirmContext);
